import { Contact } from './contact.js';
import { Notification } from '../services/Notification.js';
import { translate } from './i18n.js';

export class EventReport extends Contact {
    constructor(app, options = {}) {
        super(app, options);
        this.event = null;
        this.successCloseTimer = null;
    }

    getContents() {
        return {
            'event-report': {
                titleKey: 'eventReport.title',
                icon: '/images/signal-grey.svg',
                render: (context) => this.render(context),
                cleanup: () => this.cleanup()
            }
        };
    }

    open(event) {
        if (!event?.uid || !this.app?.simpleModal) {
            return false;
        }

        this.event = event;
        return this.app.simpleModal.open('event-report');
    }

    render(context) {
        const { body, locale } = context;
        const intro = document.createElement('p');
        const eventContext = document.createElement('p');
        const eventName = this.getEventName(locale);

        body.classList.add('contact-modal-body', 'event-report-modal-body');
        intro.className = 'contact-intro';
        intro.textContent = translate('eventReport.intro', locale);
        eventContext.className = 'event-report-context';
        const eventNameElement = document.createElement('strong');

        eventNameElement.textContent = eventName;
        eventContext.append(eventNameElement);
        body.replaceChildren(intro, eventContext, this.createForm(context));
    }

    createForm(context) {
        const form = super.createForm(context);
        const message = form.querySelector('[name="message"]')?.closest('.contact-field');
        const reason = this.createReasonField(context.locale);

        form.classList.add('event-report-form');
        if (message) {
            form.insertBefore(reason, message);
        } else {
            form.prepend(reason);
        }

        return form;
    }

    createReasonField(locale) {
        const wrapper = document.createElement('div');
        const label = document.createElement('label');
        const select = document.createElement('select');
        const reasons = ['inappropriate', 'incorrect', 'ownership', 'deletion', 'other'];

        wrapper.className = 'ui-form-element contact-field';
        label.className = 'ui-input-label';
        label.htmlFor = 'event-report-reason';
        label.textContent = translate('eventReport.reason', locale);
        select.id = 'event-report-reason';
        select.className = 'ui-select';
        select.name = 'reason';
        select.required = true;
        const placeholder = document.createElement('option');

        placeholder.value = '';
        placeholder.textContent = translate('eventReport.chooseReason', locale);
        placeholder.selected = true;
        placeholder.disabled = true;
        select.append(placeholder);
        reasons.forEach((reason) => {
            const option = document.createElement('option');

            option.value = reason;
            option.textContent = translate(`eventReport.reasons.${reason}`, locale);
            select.append(option);
        });
        this.bindValueState(select);
        wrapper.append(label, select);

        return wrapper;
    }

    createMessageField(locale) {
        const field = super.createMessageField(locale);
        const label = field.querySelector('.ui-input-label');

        if (label) {
            label.textContent = translate('eventReport.message', locale);
        }

        return field;
    }

    createSubmit(locale) {
        const button = super.createSubmit(locale);
        const icon = button.querySelector('img');
        const label = button.querySelector('span');

        if (icon) {
            icon.src = '/images/signal-grey.svg';
        }
        if (label) {
            label.textContent = translate('eventReport.send', locale);
        }

        return button;
    }

    async submit(event, context) {
        event.preventDefault();
        const form = event.currentTarget;

        if (!form.reportValidity() || !this.event?.uid) {
            return false;
        }

        const data = new FormData(form);

        data.append('event_uid', this.event.uid);
        data.append('from_web', '1');
        data.append('_silentErrors', 'true');
        const sent = await this.api.query(
            'post',
            '/events/report',
            data,
            () => {
                this.setBusy(form, true, context.locale);
                context.showLoading();
            },
            () => {
                context.hideLoading();
            },
            null,
            {
                onError: () => {
                    context.hideLoading();
                    this.setBusy(form, false, context.locale);
                }
            }
        );

        if (sent) {
            this.renderSuccess(context, {
                titleKey: 'eventReport.successTitle',
                messageKey: 'eventReport.successMessage'
            });
            this.event = null;
            window.clearTimeout(this.successCloseTimer);
            this.successCloseTimer = window.setTimeout(() => context.close(), 2500);
            return true;
        }

        this.setBusy(form, false, context.locale);
        new Notification({
            icon: 'warning.svg',
            title: translate('eventReport.errorTitle', context.locale),
            content: translate('eventReport.errorMessage', context.locale),
            class: 'no-icon-filter'
        });

        return false;
    }

    setBusy(form, busy, locale) {
        super.setBusy(form, busy, locale);
        const label = form.querySelector('[type="submit"] span');

        if (label) {
            label.textContent = translate(busy ? 'eventReport.sending' : 'eventReport.send', locale);
        }
    }

    getEventName(locale) {
        const name = this.event?.name;

        if (!name || typeof name !== 'object') {
            return String(name || '');
        }

        return name[locale] || name.fr || Object.values(name)[0] || '';
    }

    cleanup() {
        window.clearTimeout(this.successCloseTimer);
        this.successCloseTimer = null;
        this.event = null;
    }
}
